/**
 * 로딩 스피너 컴포넌트
 *
 * 데이터 로딩, 처리 중 등의 상태를 시각적으로 나타내는 애니메이션 스피너입니다.
 * 세 가지 크기와 선택적 텍스트 라벨을 지원합니다.
 *
 * @component
 * @example
 * // 기본 스피너
 * <AppLoadingSpinner />
 *
 * @example
 * // 로딩 텍스트 포함
 * <AppLoadingSpinner size="lg" text="데이터를 불러오는 중..." />
 *
 * @example
 * // 작은 스피너
 * <AppLoadingSpinner size="sm" text="처리 중" />
 *
 * @typedef {object} AppLoadingSpinnerProps
 * @property {'sm' | 'md' | 'lg'} [size='md'] - 스피너 크기 (sm: 16x16px, md: 32x32px, lg: 48x48px)
 * @property {string} [text] - 스피너 아래에 표시할 로딩 텍스트
 *
 * @returns {JSX.Element} 애니메이션이 적용된 로딩 스피너
 */

export interface AppLoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
}

export const AppLoadingSpinner = ({ size = 'md', text }: AppLoadingSpinnerProps) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <div className="flex flex-col items-center justify-center gap-3">
      <div className={`${sizeClasses[size]} animate-spin`}>
        <svg
          className="w-full h-full text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
      {text && <p className={`${textSizeClasses[size]} text-neutral-600`}>{text}</p>}
    </div>
  );
};
